import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import GenderIcon from '../components/common/GenderIcon';
import ShareModal from '../components/Pet/ShareModal';
import TagList from '../components/Pet/TagList';
import LocalizedLink from '../components/common/LocalizedLink';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';
import PetService from '../services/petService';
import { formatAge, getMealLabel, getFrequencyLabel } from '../utils/petCalculations';

const {
  FiEdit, FiUser, FiHeart, FiActivity, FiShield, FiArrowLeft,
  FiCalendar, FiPhone, FiMapPin, FiShare2, FiClock, FiMail
} = FiIcons;

// ✅ 全部移到組件外部
const petService = new PetService();

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const COLOR_CLASSES = {
  gray:   'bg-gray-50 border-gray-200',
  blue:   'bg-blue-50 border-blue-200',
  green:  'bg-green-50 border-green-200',
  purple: 'bg-purple-50 border-purple-200',
  orange: 'bg-orange-50 border-orange-200',
  red:    'bg-red-50 border-red-200',
};

const SECTION_COLOR_CLASSES = {
  primary: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  blue:    'bg-blue-100 text-blue-600 border-blue-200',
  green:   'bg-green-100 text-green-600 border-green-200',
  purple:  'bg-purple-100 text-purple-600 border-purple-200',
  orange:  'bg-orange-100 text-orange-600 border-orange-200',
  red:     'bg-red-100 text-red-600 border-red-200',
};

const InfoSection = React.memo(({ title, icon, children, className = '', colorScheme = 'primary' }) => (
  <motion.div variants={itemVariants} className={`bg-white rounded-3xl shadow-lg p-8 border border-neutral-100 hover:shadow-xl transition-shadow duration-300 ${className}`}>
    <div className="flex items-center space-x-3 mb-6">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 shadow-sm ${SECTION_COLOR_CLASSES[colorScheme]}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <h3 className="text-xl font-bold text-neutral-800">{title}</h3>
    </div>
    {children}
  </motion.div>
));

const InfoItem = React.memo(({ label, value, icon, colorScheme = 'gray', useTagList = false, tagColor = 'indigo', emptyText }) => {
  if (!value) return null;
  return (
    <div className={`p-4 rounded-2xl border shadow-sm hover:shadow-md transition-shadow duration-300 ${COLOR_CLASSES[colorScheme] || COLOR_CLASSES.gray}`}>
      <div className="flex items-center mb-2">
        {icon && <SafeIcon icon={icon} className="mr-2 text-current opacity-70" />}
        <dt className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{label}</dt>
      </div>
      <dd className="text-neutral-800 leading-relaxed pl-6 font-medium">
        {useTagList
          ? <TagList value={value} color={tagColor} emptyText={emptyText || '-'} />
          : <span className="whitespace-pre-wrap break-words">{value}</span>
        }
      </dd>
    </div>
  );
});

const PetProfilePage = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const loadPet = useCallback(async () => {
    setLoading(true);
    try {
      const petData = await petService.getPetById(id);
      setPet(petData || null);
    } catch (error) {
      console.error('載入寵物資料錯誤:', error);
      setPet(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPet(); }, [loadPet]);

  // ✅ 改為 useMemo，語言切換才重算
  const notFilledText = useMemo(() => t('petProfile.notFilled'), [t]);

  const formattedAge = useMemo(() => {
    if (!pet) return '';
    return formatAge(pet, t);
  }, [pet, t]);

  const feedingPlanContent = useMemo(() => {
    const plan = pet?.feeding_plan;
    if (!plan || !Array.isArray(plan) || plan.length === 0) return null;
    return (
      <div className="mb-6 overflow-hidden rounded-2xl border border-red-100 shadow-sm">
        <div className="bg-red-50/50 px-4 py-2 border-b border-red-100 flex items-center">
          <SafeIcon icon={FiClock} className="text-red-400 mr-2 w-4 h-4" />
          <span className="text-sm font-semibold text-red-800">{t('petProfile.feedingSchedule')}</span>
        </div>
        <table className="min-w-full divide-y divide-red-100">
          <thead className="bg-red-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.time')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.portionSize')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.foodType')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-red-50">
            {[...plan].sort((a, b) => a.time.localeCompare(b.time)).map((item, idx) => (
              <tr key={idx} className="hover:bg-red-50/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">
                  {item.time}
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                    {getMealLabel(item.time, t)}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.portion || '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.food || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [pet?.feeding_plan, t]);

  const medicationPlanContent = useMemo(() => {
    const plan = pet?.medication_plan;
    if (!plan || !Array.isArray(plan) || plan.length === 0) return null;
    return (
      <div className="mb-6 overflow-hidden rounded-2xl border border-purple-100 shadow-sm">
        <div className="bg-purple-50/50 px-4 py-2 border-b border-purple-100 flex items-center">
          <SafeIcon icon={FiClock} className="text-purple-400 mr-2 w-4 h-4" />
          <span className="text-sm font-semibold text-purple-800">{t('petProfile.medicationSchedule')}</span>
        </div>
        <table className="min-w-full divide-y divide-purple-100">
          <thead className="bg-purple-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.medicationName')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.dosage')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.frequency')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.time')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-purple-50">
            {plan.map((item, idx) => (
              <tr key={idx} className="hover:bg-purple-50/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">{item.name}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.dosage || '-'}</td>
                {/* ✅ 使用從 petCalculations 引入的 getFrequencyLabel，不再在組件內重建 */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{getFrequencyLabel(item.frequency, t)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {item.times && Array.isArray(item.times) ? (
                    <div className="flex flex-wrap gap-1">
                      {[...item.times].sort().map((time, i) => (
                        <span key={i} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                          {time}
                        </span>
                      ))}
                    </div>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [pet?.medication_plan, t]);

  const handleOpenShare = useCallback(() => setShowShareModal(true), []);
  const handleCloseShare = useCallback(() => setShowShareModal(false), []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary-200 border-t-primary-600 mb-6 mx-auto"></div>
          <p className="text-neutral-700 text-lg font-medium">{t('petProfile.loadingPet')}</p>
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center text-center p-4">
        <div className="w-20 h-20 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiUser} className="text-3xl text-neutral-400" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-neutral-800">{t('petProfile.petNotFound')}</h2>
        <p className="text-neutral-600 mb-8">{t('petProfile.petNotFoundDesc')}</p>
        <LocalizedLink to="/dashboard" className="inline-flex items-center text-neutral-600 hover:text-primary-600 group transition-colors duration-300">
          <SafeIcon icon={FiArrowLeft} className="mr-2 h-5 w-5 transform group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="font-semibold text-lg">{t('petProfile.backToDashboard')}</span>
        </LocalizedLink>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="pt-20 sm:pt-24">
        <div className="bg-gradient-to-br from-neutral-50 to-primary-50/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">

            <div className="py-8">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                <LocalizedLink to="/dashboard" className="inline-flex items-center text-neutral-600 hover:text-primary-600 group transition-colors duration-300">
                  <SafeIcon icon={FiArrowLeft} className="mr-2 h-5 w-5 transform group-hover:-translate-x-1 transition-transform duration-300" />
                  <span className="font-semibold text-lg">{t('petProfile.backToDashboard')}</span>
                </LocalizedLink>
              </motion.div>
            </div>

            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              {/* Header Card */}
              <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-neutral-100">
                <div className="md:flex">
                  <div className="md:w-2/5 relative min-h-[320px]">
                    {pet.photo_url ? (
                      <img
                        src={pet.photo_url}
                        alt={pet.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`absolute inset-0 bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center ${pet.photo_url ? 'hidden' : 'flex'}`}>
                      <SafeIcon icon={FiUser} className="text-6xl text-neutral-400" />
                    </div>
                  </div>
                  <div className="md:w-3/5 p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="flex items-center space-x-4 mb-3">
                          <h1 className="text-4xl font-bold text-neutral-800">{pet.name}</h1>
                          <GenderIcon gender={pet.gender} petType={pet.type} size="xl" />
                        </div>
                        <div className="flex flex-wrap gap-4 text-lg text-neutral-600 mb-4">
                          <span className="flex items-center">
                            <SafeIcon icon={FiUser} className="mr-2" />
                            {pet.breed || t('petProfile.notSpecified')}
                          </span>
                          <span className="flex items-center">
                            <SafeIcon icon={FiCalendar} className="mr-2" />
                            {formattedAge}
                          </span>
                          {pet.weight && (
                            <span className="flex items-center">
                              <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded mr-2">
                                {t('petProfile.weight')}
                              </span>
                              <TagList value={pet.weight} color="green" emptyText="-" />
                            </span>
                          )}
                        </div>
                        {pet.current_location && (
                          <div className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                            <SafeIcon icon={FiMapPin} className="mr-1.5 w-4 h-4" />
                            {pet.current_location}
                          </div>
                        )}
                      </div>
                      <LocalizedLink
                        to={`/pet/edit/${pet.id}`}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-2xl transition-colors flex items-center space-x-2 shadow-lg hover:shadow-xl"
                      >
                        <SafeIcon icon={FiEdit} className="text-lg" />
                        <span className="font-medium">{t('petProfile.edit')}</span>
                      </LocalizedLink>
                    </div>
                    {pet.special_notes && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6">
                        <h4 className="font-bold text-amber-800 mb-3 flex items-center">
                          <SafeIcon icon={FiHeart} className="mr-2" />
                          {t('petProfile.specialNotes')}
                        </h4>
                        <p className="text-amber-700 leading-relaxed">{pet.special_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <InfoSection title={t('petProfile.basicInfo')} icon={FiUser} colorScheme="blue">
                  <div className="grid grid-cols-1 gap-4">
                    {pet.birth_date && (
                      <InfoItem label={t('petProfile.birthDate')} value={new Date(pet.birth_date).toLocaleDateString()} colorScheme="blue" />
                    )}
                    <InfoItem label={t('petProfile.microchipId')}      value={pet.microchip_id}    colorScheme="blue" />
                    <InfoItem label={t('petProfile.registrationId')}   value={pet.registration_id} colorScheme="purple" />
                    <InfoItem label={t('petProfile.color')}            value={pet.color}            useTagList tagColor="indigo"  colorScheme="green"  emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.height')}           value={pet.height}           useTagList tagColor="orange"  colorScheme="orange" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.coatType')}         value={pet.coat_type}        useTagList tagColor="purple"  colorScheme="purple" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.eyeColor')}         value={pet.eye_color}        useTagList tagColor="teal"    colorScheme="blue"   emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.bodySize')}         value={pet.body_size}        useTagList tagColor="blue"    colorScheme="blue"   emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.specialMarkings')}  value={pet.special_markings} useTagList tagColor="pink"    colorScheme="purple" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.currentLocation')}  value={pet.current_location} icon={FiMapPin} colorScheme="red" />
                  </div>
                </InfoSection>

                <InfoSection title={t('petProfile.dietaryInfo')} icon={FiHeart} colorScheme="red">
                  <div className="flex flex-col gap-6">
                    {feedingPlanContent || (
                      <InfoItem label={t('petProfile.feedingSchedule')} value={pet.feeding_schedule} colorScheme="blue" />
                    )}
                    <InfoItem label={t('petProfile.canEat')}    value={pet.can_eat}    useTagList tagColor="green" colorScheme="green" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.cannotEat')} value={pet.cannot_eat} useTagList tagColor="red"   colorScheme="red"   emptyText={notFilledText} />
                  </div>
                </InfoSection>

                <InfoSection title={t('petProfile.behaviorInfo')} icon={FiActivity} colorScheme="green">
                  <div className="flex flex-col gap-6">
                    <InfoItem label={t('petProfile.temperament')}   value={pet.temperament}    useTagList tagColor="yellow" colorScheme="purple" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.likes')}         value={pet.likes}          useTagList tagColor="pink"   colorScheme="green"  emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.dislikes')}      value={pet.dislikes}       useTagList tagColor="orange" colorScheme="orange" emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.exerciseNeeds')} value={pet.exercise_needs} useTagList tagColor="teal"   colorScheme="blue"   emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.trainingNotes')} value={pet.training_notes} colorScheme="purple" />
                  </div>
                </InfoSection>

                <InfoSection title={t('petProfile.healthInfo')} icon={FiShield} colorScheme="purple">
                  <div className="flex flex-col gap-6">
                    {medicationPlanContent || (
                      <>
                        <InfoItem label={t('petProfile.medications')}        value={pet.medications}         colorScheme="blue" />
                        <InfoItem label={t('petProfile.medicationSchedule')} value={pet.medication_schedule} colorScheme="green" />
                      </>
                    )}
                    <InfoItem label={t('petProfile.vaccines')}          value={pet.vaccines}           useTagList tagColor="blue"   colorScheme="green"  emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.nextVaccineDate')}   value={pet.next_vaccine_date}  colorScheme="orange" />
                    <InfoItem label={t('petProfile.allergies')}         value={pet.allergies}          useTagList tagColor="orange" colorScheme="red"    emptyText={notFilledText} />
                    <InfoItem label={t('petProfile.medicalConditions')} value={pet.medical_conditions} useTagList tagColor="red"    colorScheme="red"    emptyText={notFilledText} />
                  </div>
                </InfoSection>

                {/* Vet Info - full width */}
                <InfoSection title={t('petProfile.veterinarianInfo')} icon={FiPhone} className="lg:col-span-2" colorScheme="orange">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {pet.vet_name  && <InfoItem label={t('petProfile.vetName')}  value={pet.vet_name}  useTagList tagColor="orange" icon={FiUser}  colorScheme="orange" emptyText={notFilledText} />}
                      {pet.vet_phone && <InfoItem label={t('petProfile.vetPhone')} value={pet.vet_phone} useTagList tagColor="red"    icon={FiPhone} colorScheme="red"    emptyText={notFilledText} />}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {pet.vet_address && <InfoItem label={t('petProfile.vetAddress')} value={pet.vet_address} useTagList tagColor="blue"   icon={FiMapPin} colorScheme="blue"   emptyText={notFilledText} />}
                      {pet.vet_email   && <InfoItem label={t('petProfile.vetEmail')}   value={pet.vet_email}   useTagList tagColor="purple" icon={FiMail}   colorScheme="purple" emptyText={notFilledText} />}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {pet.insurance_provider && <InfoItem label={t('petProfile.insuranceProvider')} value={pet.insurance_provider} useTagList tagColor="blue"   colorScheme="blue"   emptyText={notFilledText} />}
                      {pet.insurance_policy   && <InfoItem label={t('petProfile.insurancePolicy')}   value={pet.insurance_policy}   useTagList tagColor="purple" colorScheme="purple" emptyText={notFilledText} />}
                    </div>
                  </div>
                </InfoSection>
              </div>

              {/* Share Button */}
              <motion.div variants={itemVariants} className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleOpenShare}
                  className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:bg-primary-700 transition-all duration-300"
                >
                  <SafeIcon icon={FiShare2} className="mr-3 text-xl" />
                  {t('petProfile.shareCareInstructions')}
                </button>
              </motion.div>
            </motion.div>

            <ShareModal isOpen={showShareModal} onClose={handleCloseShare} pet={pet} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetProfilePage;